import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { SEOHead, buildOfferSchema } from '../components/SEOHead';
import { CheckCircle, MessageCircle, Star, Zap } from 'lucide-react';

export default function PricingPage() {
  const { t, language } = useLanguage();

  // Dynamic pricing based on current date
  const now = new Date();
  const month = now.getMonth() + 1;
  const isEarlyBird = month >= 5 && month <= 6;
  const currentTier = isEarlyBird ? 'early_bird' : 'normal';
  const currentPrice = isEarlyBird ? 1390 : 1495;

  const annualFeatures = [
    language === 'es' ? '30 semanas de clases (3 trimestres)' : '30 weeks of classes (3 trimesters)',
    language === 'es' ? 'Sesiones de 50 minutos' : '50-minute sessions',
    language === 'es' ? 'Grupos reducidos (máx. 8)' : 'Small groups (max. 8)',
    language === 'es' ? 'Profesores nativos' : 'Native teachers',
    language === 'es' ? 'Material didáctico incluido' : 'Teaching materials included',
    language === 'es' ? 'Acceso a plataforma virtual' : 'Virtual platform access',
    language === 'es' ? 'Clase presencial trimestral*' : 'Quarterly in-person class*'
  ];

  const trimestralFeatures = [
    language === 'es' ? '10 semanas de clases (1 trimestre)' : '10 weeks of classes (1 trimester)',
    language === 'es' ? 'Sesiones de 50 minutos' : '50-minute sessions',
    language === 'es' ? 'Grupos reducidos (máx. 8)' : 'Small groups (max. 8)',
    language === 'es' ? 'Profesores nativos' : 'Native teachers',
    language === 'es' ? 'Material didáctico incluido' : 'Teaching materials included',
    language === 'es' ? 'Acceso a plataforma virtual' : 'Virtual platform access',
    language === 'es' ? 'Sin compromiso anual' : 'No annual commitment'
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        titleES="Precios y Planes — Colegio ARCE | Desde $1,390/año"
        titleEN="Pricing & Plans — Colegio ARCE | From $1,390/year"
        descES="Planes de inscripción en Colegio ARCE: Early Bird $1,390/año, Normal $1,495/año, o Trimestral $620/trimestre. Descuento del 20% para hermanos. Pago completo o en cuotas."
        descEN="Enrollment plans at Colegio ARCE: Early Bird $1,390/year, Normal $1,495/year, or Trimester $620/trimester. 20% sibling discount. Full payment or installments."
        path="/pricing"
        schema={buildOfferSchema()}
        breadcrumbs={[
          { nameES: 'Inicio', nameEN: 'Home', path: '/' },
          { nameES: 'Precios', nameEN: 'Pricing', path: '/pricing' }
        ]}
      />
      <Navbar />
      
      <section className="pt-40 pb-20 px-6 bg-[#F5F5F7]">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm tracking-wide uppercase text-[#E63946] font-semibold mb-4">
            {t('pricing.title')}
          </p>
          <h1 className="text-4xl sm:text-5xl font-semibold text-[#1D1D1F] mb-6" data-testid="pricing-page-title">
            {t('pricing.subtitle')}
          </h1>
          <p className="text-lg text-[#86868B]">
            {language === 'es' 
              ? 'Precios transparentes sin sorpresas. Descuento de 20% para hermanos.'
              : 'Transparent pricing with no surprises. 20% sibling discount.'}
          </p>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            
            {/* Annual Plan (dynamic: Early Bird or Normal based on date) */}
            <div className="card-apple p-8 relative" data-testid={`pricing-card-${currentTier}`}>
              <div className="absolute -top-3 left-6">
                {isEarlyBird && (
                  <span className="inline-flex items-center gap-1 bg-[#34C759] text-white text-xs font-semibold px-3 py-1 rounded-full">
                    <Star className="h-3 w-3" />
                    {t('pricing.bestValue')}
                  </span>
                )}
              </div>
              <p className="text-sm tracking-wide uppercase text-[#86868B] mb-2 mt-2">
                {isEarlyBird ? t('pricing.earlyBird') : t('pricing.normal')}
              </p>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-5xl font-bold text-[#1D1D1F]">${currentPrice.toLocaleString()}</span>
                <span className="text-[#86868B]">{t('pricing.perYear')}</span>
              </div>
              <p className="text-sm text-[#86868B] mb-8">
                {isEarlyBird ? t('pricing.earlyBirdPeriod') : t('pricing.normalPeriod')}
              </p>
              
              <ul className="space-y-3 mb-6">
                {annualFeatures.map((f, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-[#34C759] flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-[#1D1D1F]">{f}</span>
                  </li>
                ))}
              </ul>

              <div className="space-y-2 mb-8 p-4 bg-[#F5F5F7] rounded-xl">
                <p className="text-xs font-semibold text-[#86868B] uppercase tracking-wide mb-2">
                  {language === 'es' ? 'Opciones de pago' : 'Payment options'}
                </p>
                <p className="text-sm text-[#1D1D1F]">
                  {language === 'es' ? 'Pago único vía Zelle' : 'Full payment via Zelle'}
                </p>
                <p className="text-sm text-[#1D1D1F]">
                  {language === 'es' ? 'O pago trimestral: 3 × $620' : 'Or trimester payment: 3 × $620'}
                </p>
                <p className="text-sm text-[#E63946] font-medium">{t('pricing.siblingDiscount')}</p>
                <p className="text-sm text-[#34C759] font-medium">
                  {language === 'es' ? 'Cupón -$100 para antiguos alumnos (pago único)' : '$100 coupon for returning students (full payment)'}
                </p>
              </div>

              <Link 
                to={`/login?redirect=/enrollment?tier=${currentTier}`}
                className="block w-full text-center py-4 rounded-full font-medium bg-[#E63946] text-white hover:bg-[#D62828] transition-colors"
                data-testid={`pricing-cta-${currentTier}`}
              >
                {t('pricing.enroll')}
              </Link>
            </div>

            {/* Trimestral */}
            <div className="card-apple p-8 relative" data-testid="pricing-card-trimestral">
              <div className="absolute -top-3 left-6">
                <span className="inline-flex items-center gap-1 bg-[#007AFF] text-white text-xs font-semibold px-3 py-1 rounded-full">
                  <Zap className="h-3 w-3" />
                  {t('pricing.flexible')}
                </span>
              </div>
              <p className="text-sm tracking-wide uppercase text-[#86868B] mb-2 mt-2">{t('pricing.trimestral')}</p>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-5xl font-bold text-[#1D1D1F]">$620</span>
                <span className="text-[#86868B]">{t('pricing.perTrimester')}</span>
              </div>
              <p className="text-sm text-[#FF9500] font-medium mb-2">{t('pricing.trimestralTotal')}</p>
              <p className="text-sm text-[#86868B] mb-8">{t('pricing.trimestralPeriod')}</p>
              
              <ul className="space-y-3 mb-6">
                {trimestralFeatures.map((f, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-[#34C759] flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-[#1D1D1F]">{f}</span>
                  </li>
                ))}
              </ul>

              <div className="space-y-2 mb-8 p-4 bg-[#F5F5F7] rounded-xl">
                <p className="text-xs font-semibold text-[#86868B] uppercase tracking-wide mb-2">
                  {language === 'es' ? 'Pago' : 'Payment'}
                </p>
                <p className="text-sm text-[#1D1D1F]">{t('pricing.trimestralSingle')}</p>
                <p className="text-sm text-[#1D1D1F] italic">{t('pricing.trimestralNote')}</p>
                <p className="text-sm text-[#E63946] font-medium">{t('pricing.siblingDiscount')}</p>
              </div>

              <Link 
                to="/login?redirect=/enrollment?tier=trimestral"
                className="block w-full text-center py-4 rounded-full font-medium bg-[#F5F5F7] text-[#1D1D1F] hover:bg-[#EBEBEF] transition-colors"
                data-testid="pricing-cta-trimestral"
              >
                {t('pricing.enroll')}
              </Link>
            </div>
          </div>

          {/* Comparison note */}
          <div className="mt-10 p-6 bg-[#FFF8E1] rounded-2xl text-center">
            <p className="text-sm text-[#1D1D1F]">
              <strong>{language === 'es' ? 'Ahorra inscribiéndote al año completo:' : 'Save by enrolling for the full year:'}</strong>{' '}
              {language === 'es' 
                ? 'El plan trimestral cuesta $1,860 si se completa el año ($620 × 3), frente a $1,390–$1,495 con los planes anuales. ¡Hasta $470 de ahorro!'
                : 'The trimester plan costs $1,860 for a full year ($620 × 3), vs. $1,390–$1,495 with annual plans. Save up to $470!'}
            </p>
          </div>

          {/* Payment method note */}
          <div className="mt-4 p-6 bg-[#F5F5F7] border border-black/5 rounded-2xl text-center">
            <p className="text-sm text-[#1D1D1F]">
              {language === 'es' 
                ? 'Pago disponible vía Zelle. Instrucciones y código QR en el formulario de inscripción.'
                : 'Payment available via Zelle. Instructions and QR code provided during enrollment.'}
            </p>
          </div>

          <div className="mt-8 text-center">
            <p className="text-sm text-[#86868B]">
              * {language === 'es' 
                ? 'Clase presencial para estudiantes locales en Medford, MA. Estudiantes fuera del estado reciben una clase extra online.'
                : 'In-person class for local students in Medford, MA. Out-of-state students receive an extra online class.'}
            </p>
          </div>
        </div>
      </section>

      <section className="section-padding bg-[#F5F5F7]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-semibold text-[#1D1D1F] mb-4">
            {language === 'es' ? '¿Tienes preguntas?' : 'Have questions?'}
          </h2>
          <p className="text-[#86868B] mb-8">
            {language === 'es'
              ? 'Agenda una cita gratuita para conocer mejor nuestro programa y resolver todas tus dudas.'
              : 'Schedule a free consultation to learn more about our program and answer all your questions.'}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="https://scheduler.zoom.us/laura-pallares-nieto/informacion-colegio-arce" target="_blank" rel="noopener noreferrer" className="btn-primary">
              {t('hero.cta2')}
            </a>
            <a href="https://wa.me/13392277556" target="_blank" rel="noopener noreferrer" className="btn-whatsapp" data-testid="pricing-whatsapp">
              <MessageCircle className="h-5 w-5" />
              WhatsApp
            </a>
          </div>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
            <Link to="/courses" className="text-[#E63946] font-medium text-sm hover:underline" data-testid="pricing-link-courses">
              {language === 'es' ? 'Ver todos los cursos →' : 'View all courses →'}
            </Link>
            <Link to="/team" className="text-[#E63946] font-medium text-sm hover:underline" data-testid="pricing-link-team">
              {language === 'es' ? 'Conocer al equipo →' : 'Meet the team →'}
            </Link>
            <Link to="/about" className="text-[#E63946] font-medium text-sm hover:underline" data-testid="pricing-link-about">
              {language === 'es' ? 'Sobre nosotros →' : 'About us →'}
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
